import React, { useEffect, useState } from 'react';
import { builderSurfaceJsonContentHeader } from '../../../../common/utils/restApiHeaderUtils';
import axios from 'axios';
import { getFieldInfoByName } from '../collection/Util';
import { ErrorMessage, Field, Formik, Form } from 'formik';
import FormFieldWithLabel from '../../../common/FormFieldWithLabel';
import CustomSelect from '../../../../common/formElements/CustomSelect';
import CustomLoader from '../../../common/CustomLoader';
import { MappingForm } from './MappingForm';
import EnvironmentSelect from '../../../../common/formElements/EnvironmentSelect';

export const OAuth2LoginForm = ({
  hideModal,
  actions,
  eventAction,
  actionData,
  selectedEvent,
  showLoaderForAction,
  projectId,
  events,
  version,
}) => {
  const [roleOptions, setRoleOptions] = useState([]);
  const [errorRedirectUrl, setErrorRedirectUrl] = useState('');
  const [allSuccessRedirectRules, setAllSuccessRedirectRules] = useState([]);
  const [successRedirectRules, setSuccessRedirectRules] = useState([
    { id: 0, key: '', value: '', environment: '' },
  ]);
  const [envRefreshCounter, setEnvRefreshCounter] = useState(0);

  useEffect(() => {
    const currentEnvironment = getCurrentEnvironment();
    const uri = `${process.env.REACT_APP_BUILDER_ENGINE}v${version}/items/role/`;
    const header = builderSurfaceJsonContentHeader(projectId);
    axios.get(uri, header).then((res) => {
      const roles = res.data;
      const roleOptions = roles.map((role) => ({ value: `${role.uuid}`, label: `${role.name}` }));
      setRoleOptions(roleOptions);
    });
    actionData.args.forEach((arg) => {
      const dbParameters = eventAction.parameters.find((param) => param.name === arg.name);
      if (dbParameters) {
        if (arg.name === 'errorRedirectUrl') setErrorRedirectUrl(dbParameters.value);
        if (arg.name === 'successRedirectRules') {
          const rules = dbParameters.value || [];
          setAllSuccessRedirectRules(rules);
          const filtered = hasEnvironmentKey(rules)
            ? rules.filter((rule) => rule.environment === currentEnvironment)
            : rules;
          setSuccessRedirectRules([
            ...filtered.map((r, i) => ({ ...r, id: `${r.key}-${r.environment}-${i}` })),
            { id: `new-${Date.now()}`, key: '', value: '', environment: currentEnvironment },
          ]);
        }
      }
    });
  }, [actionData, envRefreshCounter]);

  const pagesOptions = events.pages.map((item) => ({
    value: '/' + item.slug,
    label: item.name,
  }));

  const createActionSettingsSchema = (actionData, eventAction) => {
    let schema = {};
    const currentEnvironment = getCurrentEnvironment();
    actionData.args.forEach((args) => {
      const dbParameters = eventAction.parameters.find((param) => param.name === args.name);
      if (args.name === 'successRedirectRules') {
        const rules = dbParameters && dbParameters.value ? dbParameters.value : [];
        const filtered = hasEnvironmentKey(rules)
          ? rules.filter((rule) => rule.environment === currentEnvironment)
          : rules;
        schema[args.name] = filtered;
      } else {
        schema[args.name] = dbParameters ? dbParameters.value : '';
      }
    });
    return schema;
  };

  const hasEnvironmentKey = (arr = []) =>
    arr.some((rule) => Object.prototype.hasOwnProperty.call(rule, 'environment'));

  const refreshMethod = () => {
    setEnvRefreshCounter((prev) => prev + 1);
  };

  const getCurrentEnvironment = () => {
    return localStorage.getItem('environment') || 'PRODUCTION';
  };

  const handleSubmit = (values) => {
    let parameters = [];
    const currentEnvironment = getCurrentEnvironment();
    const newEnvMapping = successRedirectRules
      .filter((row) => row.value && !['', undefined].includes(row.value))
      .map((row) => ({
        key: row.key,
        value: row.value,
        environment: currentEnvironment,
      }));
    const mergedRules = [
      ...allSuccessRedirectRules.filter((r) => r.environment !== currentEnvironment),
      ...newEnvMapping,
    ];
    values = { ...values, successRedirectRules: mergedRules };
    ['key', 'value'].forEach((key) => {
      if (values[key]) delete values[key];
    });
    Object.keys(values).map((value) => {
      const action = actionData.args.find((param) => {
        return param.name === value;
      });
      parameters.push({
        name: value,
        value: values[value],
        type: action && action.type ? action.type : 'dropdown',
      });
    });
    let actionFormData = eventAction;
    actionFormData.parameters = parameters;
    actions.updateAction({
      projectId,
      version,
      eventId: selectedEvent.uuid,
      data: actionFormData,
    });
    hideModal();
  };

  return (
    <Formik
      enableReinitialize={true}
      initialValues={createActionSettingsSchema(actionData, eventAction)}
      onSubmit={(values, { setSubmitting, resetForm }) => {
        handleSubmit(values);
      }}
    >
      {({ isSubmitting, values, setFieldValue, setFieldTouched, errors }) => (
        <Form>
          <div className="row">
            <div className="col-12">
              <div className="form-group">
                <FormFieldWithLabel
                  cta={false}
                  label="Select Error Page"
                  icon="select"
                  infoIcon={true}
                  infoDescription={'Select Error Page.'}
                >
                  <CustomSelect
                    name="errorRedirectUrl"
                    isMulti={false}
                    onChange={setFieldValue}
                    onBlur={setFieldTouched}
                    options={pagesOptions}
                    value={errorRedirectUrl}
                    onChangeValue={(e) => setErrorRedirectUrl(e.value)}
                  />
                  <ErrorMessage name="errorRedirectUrl">
                    {(msg) => <p className="text-error">{msg}</p>}
                  </ErrorMessage>
                </FormFieldWithLabel>
              </div>
            </div>
            <div className="col-12">
              <div className="form-group">
                <FormFieldWithLabel
                  cta={false}
                  label="Success Message"
                  icon="text"
                  infoIcon={true}
                  infoDescription={getFieldInfoByName('successMessage')}
                >
                  <Field
                    name="successMessage"
                    type="text"
                    placeholder="(Optional) Enter custom success message."
                    className="form-control form-control-sm"
                  />
                </FormFieldWithLabel>
              </div>
            </div>
            <div className="col-12">
              <div className="form-group">
                <FormFieldWithLabel
                  cta={false}
                  label="Error Message"
                  icon="text"
                  infoIcon={true}
                  infoDescription={getFieldInfoByName('errorMessage')}
                >
                  <Field
                    name="errorMessage"
                    type="text"
                    placeholder="(Optional) Enter custom error message."
                    className="form-control form-control-sm"
                  />
                </FormFieldWithLabel>
              </div>
            </div>
            <div className="col-12 mt-8 mb-4" style={{ zIndex: 10, position: 'relative' }}>
              <EnvironmentSelect
                actions={actions}
                projectId={projectId}
                version={version}
                refreshMethod={refreshMethod}
              />
            </div>
            <div className="col-12 px-4">
              <MappingForm
                mapping={successRedirectRules}
                setMapping={setSuccessRedirectRules}
                roleOptions={roleOptions}
                pagesOptions={pagesOptions}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-md-6 text-left">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={hideModal}
              >
                <span>
                  <i className="wb webconnect-times"></i>&nbsp;Cancel
                </span>
              </button>
            </div>
            <div className="col-md-6 text-right">
              <button type="submit" className="btn btn-theme-submit btn-sm">
                <i className="wb webconnect-save-regular"></i>&nbsp;Save&nbsp;
                <CustomLoader loading={showLoaderForAction} />
              </button>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};
